import { Link } from "react-router-dom";
import { ArrowRight, Phone, MapPin, Check, Layers } from "lucide-react";
import Reveal from "@/components/Reveal";
import SEOHead from "@/components/SEOHead";
import FaqSection from "@/components/FaqSection";
import { photoDims } from "@/lib/photos";
import { track } from "@/lib/analytics";

const PAGE_PHOTOS = [
    { src: "/photos/hh-12.webp", alt: "Home addition and patio build by Harris Homes in Tahlequah, OK" },
    { src: "/photos/hh-21.webp", alt: "Finished living space by Harris Homes, home addition builder in Tahlequah" },
    { src: "/photos/hh-16.webp", alt: "Completed residential project by Harris Homes in the Tahlequah area" },
];

const TYPES = [
    "Bedroom or bathroom additions",
    "Primary suite additions",
    "Sunrooms and expanded living areas",
    "Second-story additions",
    "Garage conversions",
];

const FAQS = [
    {
        q: "Do you take stand-alone addition projects?",
        a: "Yes. You don't need to be building a whole custom home — we take additions as their own projects across Tahlequah and Cherokee County.",
    },
    {
        q: "Will the addition match my existing home?",
        a: "That's the whole job, in our view. We plan rooflines, siding, and flooring transitions so the new space looks and lives like it was always part of the house.",
    },
    {
        q: "Do you handle permits?",
        a: "Yes. We handle design, permits, and construction — you work with one builder from the first sketch to the final walk-through.",
    },
    {
        q: "How do we get a quote?",
        a: "Call (918) 373-4341 or send a note through the contact form with a description of the space you need. We'll come look and give you an honest number.",
    },
];

export default function HomeAdditions() {
    return (
        <div data-testid="home-additions-page">
            <SEOHead
                title="Home Additions in Tahlequah, OK | Harris Homes"
                description="Room additions, second stories and garage conversions in Tahlequah, OK. Harris Homes plans, permits and builds. Call 918-373-4341 for a quote."
                canonical="https://harrishomesok.com/home-additions-tahlequah-ok"
            />

            {/* Header */}
            <section className="pt-20 md:pt-32 pb-16 md:pb-20 px-6 md:px-10">
                <div className="max-w-[1480px] mx-auto">
                    <Reveal>
                        <div className="overline mb-6">
                            <MapPin size={11} strokeWidth={1.5} className="inline mr-2 -mt-0.5" style={{ color: "var(--hh-gold)" }} />
                            Tahlequah · Cherokee County
                        </div>
                        <h1 className="font-display text-5xl sm:text-7xl md:text-[6.5rem] leading-[0.95] tracking-tight max-w-5xl">
                            Home Additions in
                            <span style={{ fontStyle: "italic", color: "var(--hh-gold)" }}> Tahlequah, Oklahoma</span>
                        </h1>
                        <p className="mt-10 max-w-2xl text-base md:text-lg leading-relaxed" style={{ color: "var(--hh-text-dim)" }}>
                            For families who love their home and their land but have simply
                            outgrown the floor plan. We design, permit, and build additions that
                            feel like part of the original house — as stand-alone projects, with
                            the same care we put into a ground-up custom home.
                        </p>
                        <div className="mt-10 flex flex-wrap gap-4">
                            <Link to="/contact" data-testid="additions-hero-cta" className="btn-gold">
                                Request an Addition Quote <ArrowRight size={14} />
                            </Link>
                            <Link to="/services" data-testid="additions-services-link" className="btn-ghost">
                                All Services
                            </Link>
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* Types */}
            <section className="py-20 md:py-28 px-6 md:px-10" style={{ background: "var(--hh-bg-2)" }}>
                <div className="max-w-[1480px] mx-auto grid md:grid-cols-12 gap-12 md:gap-16">
                    <Reveal className="md:col-span-6">
                        <div className="overline mb-5">What We Add</div>
                        <h2 className="font-display text-4xl md:text-5xl leading-[1.1] tracking-tight">
                            Types of additions we build
                        </h2>
                        <div className="gold-rule w-24 my-8" />
                        <ul className="space-y-3 max-w-md">
                            {TYPES.map((item) => (
                                <li key={item} className="flex items-start gap-3 text-sm md:text-base" style={{ color: "var(--hh-text)" }}>
                                    <Check size={16} strokeWidth={1.6} style={{ color: "var(--hh-gold)", marginTop: 2 }} />
                                    <span>{item}</span>
                                </li>
                            ))}
                        </ul>
                    </Reveal>
                    <Reveal delay={0.12} className="md:col-span-6">
                        <div className="overline mb-5">The Fit</div>
                        <h3 className="font-display text-3xl md:text-4xl leading-[1.1] tracking-tight flex items-center gap-3">
                            <Layers size={24} strokeWidth={1.2} style={{ color: "var(--hh-gold)" }} />
                            Matching your existing home
                        </h3>
                        <div className="gold-rule w-24 my-8" />
                        <div className="space-y-6 text-base md:text-lg leading-relaxed" style={{ color: "var(--hh-text-dim)" }}>
                            <p>
                                An addition fails when it looks bolted on. We match rooflines,
                                siding, and exterior proportions first, then carry the same care
                                inside — flooring transitions, trim profiles, and ceiling heights
                                that make the new room feel original.
                            </p>
                            <p>
                                You'll see the tie-in plan before we frame a single wall.
                            </p>
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* Process + cost */}
            <section className="py-24 md:py-32 px-6 md:px-10">
                <div className="max-w-[1480px] mx-auto grid md:grid-cols-12 gap-12 md:gap-16">
                    <Reveal className="md:col-span-6">
                        <div className="overline mb-5">The Process</div>
                        <h2 className="font-display text-4xl md:text-5xl leading-[1.1] tracking-tight">
                            How an addition
                            <span style={{ fontStyle: "italic", color: "var(--hh-gold)" }}> project runs.</span>
                        </h2>
                        <div className="gold-rule w-24 my-8" />
                        <p className="text-base md:text-lg leading-relaxed" style={{ color: "var(--hh-text-dim)" }}>
                            Design and tie-in planning first, permits second, then framing,
                            mechanicals, and finish work — ending with a walk-through together.
                            You get a written scope and a week-by-week schedule before work
                            starts, and straight talk if anything changes along the way.
                        </p>
                    </Reveal>
                    <Reveal delay={0.12} className="md:col-span-6">
                        <div className="overline mb-5">Budget</div>
                        <h2 className="font-display text-4xl md:text-5xl leading-[1.1] tracking-tight">
                            Cost factors,
                            <span style={{ fontStyle: "italic", color: "var(--hh-gold)" }}> honestly.</span>
                        </h2>
                        <div className="gold-rule w-24 my-8" />
                        <p className="text-base md:text-lg leading-relaxed" style={{ color: "var(--hh-text-dim)" }}>
                            Addition pricing moves with square footage, structural tie-in
                            complexity, whether plumbing is involved, and finish level. Rather
                            than guess over the phone, we walk the house, listen to what you
                            need, and give you an itemized number you can hold us to.
                        </p>
                    </Reveal>
                </div>
            </section>

            {/* Photos */}
            <section className="py-20 md:py-28 px-6 md:px-10" style={{ background: "var(--hh-bg-2)" }}>
                <div className="max-w-[1480px] mx-auto">
                    <Reveal>
                        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
                            <div>
                                <div className="overline mb-5">Our Work</div>
                                <h2 className="font-display text-4xl md:text-5xl leading-[1.05] tracking-tight max-w-2xl">
                                    Recent projects in the Tahlequah area.
                                </h2>
                            </div>
                            <Link to="/projects" data-testid="additions-projects-link" className="btn-ghost self-start">
                                Full Gallery <ArrowRight size={14} />
                            </Link>
                        </div>
                    </Reveal>
                    <div className="grid md:grid-cols-3 gap-3 md:gap-4">
                        {PAGE_PHOTOS.map((p, i) => {
                            const d = photoDims(p.src);
                            return (
                                <Reveal key={p.src} delay={i * 0.08}>
                                    <div className="relative img-zoom h-[280px] md:h-[340px]">
                                        <img
                                            src={p.src}
                                            alt={p.alt}
                                            width={d?.w}
                                            height={d?.h}
                                            className="w-full h-full object-cover"
                                            loading="lazy"
                                        />
                                    </div>
                                </Reveal>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* FAQ */}
            <FaqSection
                overline="Frequently Asked"
                headingLead="Questions people ask"
                headingItalic="about additions."
                items={FAQS}
                testId="additions-faq"
            />

            {/* CTA */}
            <section className="py-28 md:py-40 px-6 md:px-10 text-center">
                <Reveal>
                    <div className="overline mb-5">Request an Addition Quote</div>
                    <h2 className="font-display text-4xl md:text-6xl leading-tight tracking-tight max-w-3xl mx-auto">
                        Need the room your family
                        <span style={{ fontStyle: "italic", color: "var(--hh-gold)" }}> has outgrown?</span>
                    </h2>
                    <div className="mt-10 flex flex-wrap gap-4 justify-center">
                        <Link to="/contact" data-testid="additions-final-cta" onClick={() => track.ctaClick("Request an Addition Quote", "additions-final")} className="btn-gold">
                            Request a Quote <ArrowRight size={14} />
                        </Link>
                        <a href="tel:+19183734341" data-testid="additions-final-phone" onClick={() => track.phoneClick("additions-final")} className="btn-ghost">
                            <Phone size={14} strokeWidth={1.5} /> (918) 373-4341
                        </a>
                    </div>
                </Reveal>
            </section>
        </div>
    );
}
